import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import type { DiscoverFilter, DiscoverItem, DiscoverCity } from "../types";
import { useDiscoverConfig, useDiscoverSavedAdapter, type DiscoverSavedAdapter } from "../config";
import { isTrip } from "../lib/is-trip";
import { matchesSearch } from "../lib/matches-search";
import { useIsomorphicLayoutEffect } from "../lib/use-isomorphic-layout-effect";
import {
  createInitialDiscoverState,
  discoverReducer,
  type DiscoverAction,
  type DiscoverState,
  type ViewportBounds,
} from "./discover-reducer";
import { clearLikedIds, readLikedIds, writeLikedIds } from "./trip-storage";

// Base filter definitions for the tab strip.
const BASE_FILTERS: readonly { id: string; label: string; labelKey: string; icon?: string }[] = [
  { id: "for-you", label: "For you", labelKey: "discover.tab.for_you", icon: "ri-sparkling-line" },
  { id: "things-to-do", label: "Things to do", labelKey: "discover.tab.things_to_do", icon: "ri-ticket-2-line" },
  { id: "restaurants", label: "Restaurants", labelKey: "discover.tab.restaurants", icon: "ri-restaurant-line" },
  { id: "stays", label: "Stays", labelKey: "discover.tab.stays", icon: "ri-hotel-line" },
  { id: "attractions", label: "Attractions", labelKey: "discover.tab.attractions", icon: "ri-map-pin-line" },
];

function buildDiscoverFilters(items: readonly DiscoverItem[]): DiscoverFilter[] {
  // Counted over places, not over trips: these numbers sit on the browse tabs,
  // and the browse does not show multi-stop trips. A tab that promises 792 and
  // then lists 769 is a tab that has lied to the traveller before they have
  // touched anything. See lib/is-trip.ts.
  const places = items.filter((item) => !isTrip(item));
  return BASE_FILTERS.map((filter) => ({
    ...filter,
    count: places.filter((item) => item.tabs.includes(filter.id)).length,
  }));
}

interface DiscoverContextValue {
  state: DiscoverState;
  dispatch: React.Dispatch<DiscoverAction>;
  items: readonly DiscoverItem[];
  filters: readonly DiscoverFilter[];
  cities: readonly DiscoverCity[];
  isLoadingItems: boolean;
  itemsError: string | null;
  /** The host's saved list when it lends one; hearts go there instead. */
  saved: DiscoverSavedAdapter | null;
}

const DiscoverContext = createContext<DiscoverContextValue | null>(null);

export function DiscoverProvider({
  children,
  initialMode,
}: {
  children: ReactNode;
  /** The section this URL is. Seeds the reducer so the first render is right. */
  initialMode?: DiscoverState["utilityMode"];
}) {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const { dataSource } = useDiscoverConfig();
  const [items, setItems] = useState<readonly DiscoverItem[]>(() => dataSource.getInitialItems());
  const [cities, setCities] = useState<readonly DiscoverCity[]>([]);
  const [isLoadingItems, setIsLoadingItems] = useState(true);
  const [itemsError, setItemsError] = useState<string | null>(null);
  const [state, dispatch] = useReducer(discoverReducer, undefined, () =>
    createInitialDiscoverState(initialMode),
  );
  /** False until localStorage has been read. Nothing is written back before then. */
  const [likedHydrated, setLikedHydrated] = useState(false);

  // Liked ids come from localStorage, which the server cannot read — so they
  // are read after mount, not during render, or the two trees disagree and
  // React discards the client's. Same reasoning as TripProvider; the layout
  // effect lands them before the first paint so no heart flashes empty.
  useIsomorphicLayoutEffect(() => {
    const stored = readLikedIds();
    if (stored.length > 0) dispatch({ type: "SET_LIKED_IDS", ids: stored });
    setLikedHydrated(true);
  }, []);

  const saved = useDiscoverSavedAdapter();

  // Persist likedIds on change — never before the read above, or the empty
  // first render would erase them. Not when the host keeps the list.
  useEffect(() => {
    if (!likedHydrated || saved) return;
    writeLikedIds(Array.from(state.likedIds));
  }, [likedHydrated, state.likedIds, saved]);

  useEffect(() => {
    let cancelled = false;
    setIsLoadingItems(true);

    async function loadItems() {
      try {
        const { items: nextItems, cities: nextCities } = await dataSource.fetchIndex(lang);
        if (cancelled) return;
        setItems(nextItems);
        setCities(nextCities);
        setItemsError(null);
      } catch (error) {
        if (cancelled) return;
        setItemsError(error instanceof Error ? error.message : "Failed to load activities");
      } finally {
        if (!cancelled) setIsLoadingItems(false);
      }
    }

    void loadItems();

    return () => {
      cancelled = true;
    };
  }, [lang, dataSource]);

  const filters = useMemo(() => {
    const scoped = items.filter((item) => {
      if (state.selectedCitySlug && item.citySlug !== state.selectedCitySlug) return false;
      if (state.visibility === "bookable" && item.mode !== "bookable") return false;
      if (state.visibility === "explore" && item.mode !== "explore") return false;
      // The counts follow the search: "briganti" on Things to do read
      // Restaurants 305 beside an empty grid, with no hint the match was a
      // tab away.
      return matchesSearch(item, state.searchQuery);
    });
    return buildDiscoverFilters(scoped);
  }, [items, state.selectedCitySlug, state.visibility, state.searchQuery]);

  // The API's per-city `activitiesCount` is bookable-only (it's the home-page
  // counter), so it badly undercounts what Discover actually shows — a city
  // can have dozens of explore-only restaurants/attractions and a handful of
  // bookables. Recount from the loaded items (both modes, already image-
  // filtered) so the city dropdown reflects the real number of picks.
  const citiesWithCounts = useMemo<readonly DiscoverCity[]>(() => {
    const counts = new Map<string, number>();
    for (const item of items) {
      if (!item.citySlug) continue;
      // Trips excluded here too: this number is what the city dropdown offers,
      // and offering a city on the strength of rows the browse will not show
      // is how a traveller ends up picking a city and landing on less than the
      // pill promised.
      if (isTrip(item)) continue;
      counts.set(item.citySlug, (counts.get(item.citySlug) ?? 0) + 1);
    }
    return cities.map((city) => ({
      ...city,
      activitiesCount: counts.get(city.slug) ?? city.activitiesCount ?? 0,
    }));
  }, [cities, items]);

  // Hearts this browser kept before the host lent its list: carried over once
  // the items they point at have loaded, then forgotten.
  const carried = useRef(false);
  useEffect(() => {
    if (!saved || !likedHydrated || isLoadingItems || carried.current) return;
    carried.current = true;
    if (state.likedIds.size === 0) return;
    for (const item of items) {
      if (state.likedIds.has(item.id) && !saved.isSaved(item)) saved.setSaved(item, true);
    }
    clearLikedIds();
  }, [saved, likedHydrated, isLoadingItems, items, state.likedIds]);

  // With a host list, "liked" is whatever that list holds, so every heart on
  // the site and the Liked section agree.
  const effectiveState = useMemo<DiscoverState>(() => {
    if (!saved) return state;
    return { ...state, likedIds: new Set(items.filter((item) => saved.isSaved(item)).map((item) => item.id)) };
  }, [saved, state, items]);

  const value = useMemo<DiscoverContextValue>(
    () => ({
      state: effectiveState,
      dispatch,
      items,
      filters,
      cities: citiesWithCounts,
      isLoadingItems,
      itemsError,
      saved,
    }),
    [effectiveState, items, filters, citiesWithCounts, isLoadingItems, itemsError, saved],
  );

  return (
    <DiscoverContext.Provider value={value}>{children}</DiscoverContext.Provider>
  );
}

export function useDiscoverContext(): DiscoverContextValue {
  const ctx = useContext(DiscoverContext);
  if (ctx === null) {
    throw new Error(
      "useDiscoverContext must be used inside <DiscoverProvider>"
    );
  }
  return ctx;
}

// Aggregator hook matching the spec's documented API — combines state + actions.
export function useDiscover() {
  const { state } = useDiscoverContext();
  const actions = useDiscoverActions();
  return { state, actions };
}

// Convenience action creators bound to dispatch
export function useDiscoverActions() {
  const { dispatch, items, saved } = useDiscoverContext();
  const navigate = useNavigate();
  return useMemo(
    () => ({
      setUtilityMode: (mode: DiscoverState["utilityMode"]) =>
        dispatch({ type: "SET_UTILITY_MODE", mode }),
      setTab: (tab: string) => dispatch({ type: "SET_TAB", tab }),
      setVisibility: (visibility: DiscoverState["visibility"]) =>
        dispatch({ type: "SET_VISIBILITY", visibility }),
      setSearch: (query: string) => dispatch({ type: "SET_SEARCH", query }),
      // An item with its own page (a hotel we sell online) goes there instead
      // of opening the side panel: that page is where it is booked, and the
      // panel has no editorial copy for it. Every way in (card, map pin, a
      // trip stop, ?item=) comes through here.
      selectItem: (itemId: string) => {
        const href = items.find((it) => it.id === itemId)?.detailHref;
        if (href) {
          void navigate({ href });
          return;
        }
        dispatch({ type: "SELECT_ITEM", itemId });
      },
      closeDetail: () => dispatch({ type: "CLOSE_DETAIL" }),
      toggleMapMode: () => dispatch({ type: "TOGGLE_MAP_MODE" }),
      resetFilters: () => dispatch({ type: "RESET_FILTERS" }),
      toggleLiked: (itemId: string) => {
        const item = saved ? items.find((it) => it.id === itemId) : undefined;
        if (saved && item) {
          saved.setSaved(item, !saved.isSaved(item));
          return;
        }
        dispatch({ type: "TOGGLE_LIKED", itemId });
      },
      setCity: (slug: string | null) => dispatch({ type: "SET_CITY", slug }),
      setAiMatchSlugs: (slugs: readonly string[]) =>
        dispatch({ type: "SET_AI_MATCH_SLUGS", slugs }),
      setViewportBounds: (bounds: ViewportBounds | null) =>
        dispatch({ type: "SET_VIEWPORT_BOUNDS", bounds }),
      setSubtags: (subtags: readonly string[]) =>
        dispatch({ type: "SET_SUBTAGS", subtags }),
      setChatMapSize: (size: DiscoverState["chatMapSize"]) =>
        dispatch({ type: "SET_CHAT_MAP_SIZE", size }),
    }),
    [dispatch, items, navigate, saved],
  );
}

export function useDiscoverItems(): readonly DiscoverItem[] {
  return useDiscoverContext().items;
}

export function useDiscoverFilters(): readonly DiscoverFilter[] {
  return useDiscoverContext().filters;
}

export function useDiscoverCities(): readonly DiscoverCity[] {
  return useDiscoverContext().cities;
}

/**
 * Per-city Discover chrome (hero label, map embed, trip presets) sourced
 * from the API. Falls back to the active city in the cities list, then to
 * the first city. Empty strings / arrays when nothing is loaded yet.
 *
 * When DiscoverConfig.meta provides heroLabel / tripPresets, those override
 * the city-derived defaults (useful for single-city deployments).
 */
export function useDiscoverMeta() {
  const { state, cities } = useDiscoverContext();
  const { meta } = useDiscoverConfig();
  const active = state.selectedCitySlug
    ? cities.find((c) => c.slug === state.selectedCitySlug)
    : null;
  const city = active ?? cities[0] ?? null;
  return {
    city: city
      ? {
          slug: city.slug as "casablanca",
          name: city.name as "Casablanca",
          latitude: city.latitude ?? 0,
          longitude: city.longitude ?? 0,
        }
      : null,
    heroLabel: meta?.heroLabel ?? city?.discoverHeroLabel ?? "",
    mapEmbedUrl: city?.discoverMapEmbedUrl ?? "",
    tripPresets: meta?.tripPresets ?? city?.discoverTripPresets ?? [],
  };
}

export { DiscoverContext };
